import { InteriorSetRequest } from "@1games-ru/region-shared/dist/backend/requests/interior";
import { InteriorType } from "@1games-ru/region-shared/dist/enums/interior/interior";
import {
  InteriorApartment,
  InteriorComplexFloor,
  InteriorEstateGarage,
  InteriorHouse,
  InteriorMap,
} from "@1games-ru/region-shared/dist/types/interior/interior-b2";
import { useCallback, useEffect, useState } from "react";
import { Select, SelectOption } from "@/shared/ui";
import useCreateInterior from "@/domains/createEntity/hooks/useCreateInterior";
import usePoint from "@/domains/createEntity/hooks/usePoint";
import CreateInteriorApartmentFormContainer from "@/domains/createEntity/containers/forms/interior/CreateEntityInteriorApartmentFormContainer";
import CreateEntityInteriorComplexFloorFormContainer from "@/domains/createEntity/containers/forms/interior/CreateEntityInteriorComplexFloorFormContainer";
import CreateEntityInteriorEstateGarageFormContainer from "@/domains/createEntity/containers/forms/interior/CreateEntityInteriorEstateGarageFormContainer";
import { CreateEntityInteriorHouseFormContainer } from "@/domains/createEntity/containers/forms/interior/CreateEntityInteriorHouseFormContainer";
import CreateEntityRow from "@/domains/createEntity/components/CreateEntityRow";
import CreateEntityScroll from "@/domains/createEntity/components/CreateEntityScroll";
import CreateEntityTitle from "@/domains/createEntity/components/CreateEntityTitle";

type IndoorsInteriorType = InteriorSetRequest["type"];

const interiorTypeOptions = [
  InteriorType.House,
  InteriorType.Apartment,
  InteriorType.EstateGarage,
  InteriorType.ComplexFloor,
] as const;

type Props = {
  onClose?: () => void;
};

export type InteriorFormProps<T extends InteriorMap[keyof InteriorMap]> = {
  onSubmit: (payload: {
    uuid: string | null;
    name: string;
    description: string;
    content: T;
  }) => Promise<void>;
  onClear: () => void;
};

export default function CrateEntityInteriorFormContainer({ onClose }: Props) {
  const { clearPoints } = usePoint();
  const { createInterior } = useCreateInterior();
  const [type, setType] = useState<IndoorsInteriorType>(InteriorType.House);

  const clear = useCallback(async () => {
    await clearPoints();
  }, [clearPoints]);

  useEffect(() => {
    return () => {
      clear();
    };
  }, [clear]);

  const createHouseHandler = useCallback(
    async (payload: Parameters<InteriorFormProps<InteriorHouse>["onSubmit"]>[0]) => {
      await createInterior({
        uuid: payload.uuid,
        name: payload.name,
        description: payload.description,
        position: payload.content.border.position,

        type: InteriorType.House,
        content: payload.content,
      });
    },
    [createInterior],
  );

  const createApartmentHandler = useCallback(
    async (payload: Parameters<InteriorFormProps<InteriorApartment>["onSubmit"]>[0]) => {
      await createInterior({
        uuid: payload.uuid,
        name: payload.name,
        description: payload.description,
        position: payload.content.border.position,

        type: InteriorType.Apartment,
        content: payload.content,
      });
    },
    [createInterior],
  );

  const createEstateGarageHandler = useCallback(
    async (payload: Parameters<InteriorFormProps<InteriorEstateGarage>["onSubmit"]>[0]) => {
      await createInterior({
        uuid: payload.uuid,
        name: payload.name,
        description: payload.description,
        position: payload.content.border.position,

        type: InteriorType.EstateGarage,
        content: payload.content,
      });
    },
    [createInterior],
  );

  const createComplexFloorHandler = useCallback(
    async (payload: Parameters<InteriorFormProps<InteriorComplexFloor>["onSubmit"]>[0]) => {
      await createInterior({
        uuid: payload.uuid,
        name: payload.name,
        description: payload.description,
        position: payload.content.border.position,

        type: InteriorType.ComplexFloor,
        content: payload.content,
      });
    },
    [createInterior],
  );

  return (
    <>
      <CreateEntityTitle title="Интерьер" onClose={onClose} />

      <CreateEntityScroll>
        <div className="flex flex-col gap-3.5">
          <CreateEntityRow label="Тип интерьера">
            <Select className="z-3" value={type}>
              {interiorTypeOptions.map((item) => (
                <SelectOption key={item} onClick={() => setType(item)}>
                  {item}
                </SelectOption>
              ))}
            </Select>
          </CreateEntityRow>

          {type === InteriorType.House && (
            <CreateEntityInteriorHouseFormContainer onSubmit={createHouseHandler} onClear={clear} />
          )}

          {type === InteriorType.Apartment && (
            <CreateInteriorApartmentFormContainer
              onSubmit={createApartmentHandler}
              onClear={clear}
            />
          )}

          {type === InteriorType.EstateGarage && (
            <CreateEntityInteriorEstateGarageFormContainer
              onSubmit={createEstateGarageHandler}
              onClear={clear}
            />
          )}

          {type === InteriorType.ComplexFloor && (
            <CreateEntityInteriorComplexFloorFormContainer
              onSubmit={createComplexFloorHandler}
              onClear={clear}
            />
          )}
        </div>
      </CreateEntityScroll>
    </>
  );
}
